import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { useProjectStore } from '../../../store/projectStore';
import { useInventoryStore, cataloguePickerItems } from '../../../store/inventoryStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useSitesStore } from '../../../store/sitesStore';
import {
  useStockRequestStore,
  NewRequestLineInput,
  OpenItemConflict,
  formatRequestRef,
} from '../../../store/stockRequestStore';
import { getRequestTheme } from './theme';
import { StepBar, FieldLabel, QtyInput } from '../movement/shared';

const STEPS = ['What you need', 'Destination', 'Review'];

type LineRow = {
  mode: 'catalog' | 'free_text';
  itemId: string;
  freeText: string;
  qty: number;
  condition: 'new' | 'used';
};

const emptyLine = (): LineRow => ({
  mode: 'catalog',
  itemId: '',
  freeText: '',
  qty: 1,
  condition: 'new',
});

type PendingDuplicate = {
  lineIdx: number;
  itemId: string;
  conflicts: OpenItemConflict[];
  /** Keep select on the chosen item while checking / confirming — do not commit to the line yet. */
  phase: 'checking' | 'confirm';
};

interface Props {
  onClose: () => void;
  onCreated: (requestId: string) => void;
  /** Open an existing request (e.g. from duplicate warning). */
  onOpenRequest?: (requestId: string) => void;
}

const NewRequestWizard: React.FC<Props> = ({ onClose, onCreated, onOpenRequest }) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const theme = getRequestTheme(isDark);
  const { items, fetchItems } = useInventoryStore();
  const { warehouses, storageAreasByWarehouse, defaultWarehouseId } = useWarehouseStore();
  const { sites } = useSitesStore();
  const { createRequest, submitRequest, findOpenItemConflicts, error } = useStockRequestStore();

  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');
  const [siteId, setSiteId] = useState('');
  const [jobId, setJobId] = useState('');
  const [pendingDup, setPendingDup] = useState<PendingDuplicate | null>(null);

  const defaultWh = warehouses.find(w => w.id === defaultWarehouseId) ?? warehouses[0];
  const [warehouseId, setWarehouseId] = useState(defaultWh?.id ?? '');
  const [storageAreaId, setStorageAreaId] = useState('');

  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    if (!warehouseId && defaultWh) setWarehouseId(defaultWh.id);
  }, [defaultWh, warehouseId]);

  const areas = useMemo(
    () => (warehouseId ? storageAreasByWarehouse[warehouseId] ?? [] : []),
    [warehouseId, storageAreasByWarehouse],
  );

  useEffect(() => {
    if (areas.length > 0 && !areas.find(a => a.id === storageAreaId)) {
      setStorageAreaId(areas[0].id);
    }
  }, [areas, storageAreaId]);

  const close = () => { setVisible(false); setTimeout(onClose, 220); };

  const updateLine = (idx: number, patch: Partial<LineRow>) => {
    setLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleCatalogSelect = (idx: number, itemId: string) => {
    if (!itemId) {
      setPendingDup(null);
      updateLine(idx, { itemId: '' });
      return;
    }

    // Sync: pin the select to the chosen value immediately so it does not flash
    // back to empty while the conflict check runs. Do not commit to the line yet.
    setPendingDup({ lineIdx: idx, itemId, conflicts: [], phase: 'checking' });
    setLocalError('');

    void (async () => {
      try {
        const conflicts = await findOpenItemConflicts(itemId);
        if (conflicts.length === 0) {
          updateLine(idx, { itemId });
          setPendingDup(null);
          return;
        }
        setPendingDup({ lineIdx: idx, itemId, conflicts, phase: 'confirm' });
      } catch {
        updateLine(idx, { itemId });
        setPendingDup(null);
      }
    })();
  };

  const confirmDuplicateAdd = () => {
    if (!pendingDup) return;
    updateLine(pendingDup.lineIdx, { itemId: pendingDup.itemId });
    setPendingDup(null);
  };

  const cancelDuplicateAdd = () => setPendingDup(null);

  const step1Valid = lines.every(l =>
    l.qty >= 1 && (
      (l.mode === 'catalog' && l.itemId !== '') ||
      (l.mode === 'free_text' && l.freeText.trim() !== '')
    ),
  );
  const step2Valid = warehouseId !== '';

  const handleSubmit = async () => {
    setSaving(true);
    setLocalError('');

    const payload: NewRequestLineInput[] = lines.map(l => ({
      lineType: l.mode,
      itemId: l.mode === 'catalog' ? l.itemId : null,
      freeTextDescription: l.mode === 'free_text' ? l.freeText.trim() : null,
      requestedQty: l.qty,
      condition: l.condition,
    }));

    const request = await createRequest({
      warehouseId,
      storageAreaId: storageAreaId || null,
      priority,
      notes: notes.trim() || null,
      siteId: siteId || null,
      jobId: jobId || null,
      lines: payload,
    });

    if (!request) {
      setSaving(false);
      setLocalError(error ?? 'Failed to create request');
      return;
    }

    const submitted = await submitRequest(request.id);
    setSaving(false);
    if (!submitted) {
      setLocalError(error ?? 'Created but failed to submit');
      return;
    }
    onCreated(request.id);
  };

  const whName = warehouses.find(w => w.id === warehouseId)?.name ?? '';
  const areaName = areas.find(a => a.id === storageAreaId)?.name ?? '';

  const inputCls = 'w-full text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <>
      <div
        className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)', opacity: visible ? 1 : 0 }}
        onClick={close}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex flex-col shadow-2xl"
        style={{
          width: 'min(720px, 100vw)',
          backgroundColor: theme.cardBg,
          borderLeft: `1px solid ${theme.border}`,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: theme.muted }}>Stock Request</p>
            <h2 className="text-sm font-semibold mt-0.5" style={{ color: theme.text }}>New Request</h2>
          </div>
          <button type="button" onClick={close} className="p-1.5 rounded hover:opacity-70" style={{ color: theme.muted }}>
            <X size={16} />
          </button>
        </div>

        <StepBar steps={STEPS} current={step} />

        <div className="flex-1 overflow-auto px-5 py-4">
          {step === 0 && (
            <div className="space-y-3">
              <p className="text-xs" style={{ color: theme.muted }}>
                Add items from the catalogue or describe something not yet listed. Free-text lines will be matched by the approver before ordering.
              </p>
              <div className="rounded" style={{ border: `1px solid ${theme.border}` }}>
                <table className="w-full text-xs">
                  <thead style={{ backgroundColor: theme.headerBg }}>
                    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                      {['Type', 'Item', 'Qty', 'Condition', ''].map(h => (
                        <th key={h} className="px-2 py-2 text-left uppercase tracking-wide font-medium" style={{ color: theme.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: theme.cardBg }}>
                    {lines.map((line, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td className="px-2 py-2 align-middle">
                          <select
                            value={line.mode}
                            onChange={e => {
                              setPendingDup(null);
                              updateLine(idx, { mode: e.target.value as 'catalog' | 'free_text', itemId: '', freeText: '' });
                            }}
                            className={inputCls}
                            style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text, minWidth: 100 }}
                          >
                            <option value="catalog">Catalogue</option>
                            <option value="free_text">Free text</option>
                          </select>
                        </td>
                        <td className="px-2 py-2 align-top relative" style={{ minWidth: 200 }}>
                          {line.mode === 'catalog' ? (
                            <div className="relative">
                              <select
                                value={pendingDup?.lineIdx === idx ? pendingDup.itemId : line.itemId}
                                onChange={e => handleCatalogSelect(idx, e.target.value)}
                                disabled={pendingDup?.lineIdx === idx}
                                className={inputCls}
                                style={{
                                  backgroundColor: theme.inputBg,
                                  borderColor: theme.border,
                                  color: (pendingDup?.lineIdx === idx ? pendingDup.itemId : line.itemId)
                                    ? theme.text
                                    : theme.muted,
                                }}
                              >
                                <option value="">Select item…</option>
                                {cataloguePickerItems(items).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                              </select>
                              {pendingDup?.lineIdx === idx && pendingDup.phase === 'confirm' && (
                                <div
                                  className="absolute left-0 top-full z-20 mt-1 w-64 rounded-md shadow-lg p-2.5"
                                  style={{
                                    backgroundColor: isDark ? '#1e2330' : '#ffffff',
                                    border: `1px solid ${theme.border}`,
                                  }}
                                >
                                  <p className="text-[11px] leading-snug mb-2" style={{ color: theme.muted }}>
                                    Already on{' '}
                                    {pendingDup.conflicts.map((c, i) => (
                                      <React.Fragment key={c.requestId}>
                                        {i > 0 && ', '}
                                        {onOpenRequest ? (
                                          <button
                                            type="button"
                                            onClick={() => onOpenRequest(c.requestId)}
                                            className="font-medium text-blue-400 hover:underline"
                                          >
                                            {formatRequestRef(c.requestNumber)}
                                          </button>
                                        ) : (
                                          <span style={{ color: theme.text }}>
                                            {formatRequestRef(c.requestNumber)}
                                          </span>
                                        )}
                                      </React.Fragment>
                                    ))}
                                    . Add anyway?
                                  </p>
                                  <div className="flex items-center justify-end gap-1.5">
                                    <button
                                      type="button"
                                      onClick={cancelDuplicateAdd}
                                      className="px-2 py-1 rounded text-[11px] font-medium"
                                      style={{ color: theme.muted }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={confirmDuplicateAdd}
                                      className="px-2 py-1 rounded text-[11px] font-medium bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      Add anyway
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <input
                              type="text"
                              placeholder="Describe the item…"
                              value={line.freeText}
                              onChange={e => updateLine(idx, { freeText: e.target.value })}
                              className={inputCls}
                              style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                            />
                          )}
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <QtyInput
                            isDark={isDark}
                            value={line.qty}
                            min={1}
                            onChange={e => updateLine(idx, { qty: Math.max(1, parseInt(e.target.value) || 1) })}
                          />
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <select
                            value={line.condition}
                            onChange={e => updateLine(idx, { condition: e.target.value as 'new' | 'used' })}
                            className={inputCls}
                            style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                          >
                            <option value="new">New</option>
                            <option value="used">Used</option>
                          </select>
                        </td>
                        <td className="px-2 py-2 align-middle">
                          {lines.length > 1 && (
                            <button type="button" onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))} style={{ color: theme.muted }}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setLines(prev => [...prev, emptyLine()])}
                    className="inline-flex items-center gap-1 text-xs text-blue-500"
                  >
                    <Plus size={12} /> Add line
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4 max-w-md">
              <div>
                <FieldLabel>Warehouse</FieldLabel>
                <select
                  value={warehouseId}
                  onChange={e => setWarehouseId(e.target.value)}
                  className={inputCls + ' w-full mt-1'}
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                >
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              {areas.length > 0 && (
                <div>
                  <FieldLabel>Storage area</FieldLabel>
                  <select
                    value={storageAreaId}
                    onChange={e => setStorageAreaId(e.target.value)}
                    className={inputCls + ' w-full mt-1'}
                    style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                  >
                    {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <FieldLabel>Priority</FieldLabel>
                <select
                  value={priority}
                  onChange={e => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                  className={inputCls + ' w-full mt-1'}
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              {sites.length > 0 && (
                <div>
                  <FieldLabel>Related site (optional)</FieldLabel>
                  <select
                    value={siteId}
                    onChange={e => { setSiteId(e.target.value); setJobId(''); }}
                    className={inputCls + ' w-full mt-1'}
                    style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: siteId ? theme.text : theme.muted }}
                  >
                    <option value="">None</option>
                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              {siteId && (() => {
                const site = sites.find(s => s.id === siteId);
                const jobs = site?.jobs ?? [];
                if (jobs.length === 0) return null;
                return (
                  <div>
                    <FieldLabel>Related job (optional)</FieldLabel>
                    <select
                      value={jobId}
                      onChange={e => setJobId(e.target.value)}
                      className={inputCls + ' w-full mt-1'}
                      style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: jobId ? theme.text : theme.muted }}
                    >
                      <option value="">None</option>
                      {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                    </select>
                  </div>
                );
              })()}
              <div>
                <FieldLabel>Notes (optional)</FieldLabel>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  className={inputCls + ' w-full mt-1 resize-none'}
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                  placeholder="Urgency, context, etc."
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 text-xs">
              <div className="rounded p-3" style={{ backgroundColor: theme.inputBg, border: `1px solid ${theme.border}` }}>
                <p style={{ color: theme.muted }}>Destination</p>
                <p className="font-medium mt-0.5" style={{ color: theme.text }}>{whName}{areaName ? ` · ${areaName}` : ''}</p>
                <p className="mt-2" style={{ color: theme.muted }}>Priority: <span style={{ color: theme.text }}>{priority}</span></p>
                {siteId && (
                  <p className="mt-1" style={{ color: theme.muted }}>
                    Site: <span style={{ color: theme.text }}>{sites.find(s => s.id === siteId)?.name}</span>
                    {jobId && <span> · Job: <span style={{ color: theme.text }}>{sites.find(s => s.id === siteId)?.jobs?.find(j => j.id === jobId)?.title}</span></span>}
                  </p>
                )}
              </div>
              <div className="rounded overflow-hidden" style={{ border: `1px solid ${theme.border}` }}>
                <table className="w-full">
                  <thead style={{ backgroundColor: theme.headerBg }}>
                    <tr>
                      {['Item', 'Qty', 'Condition'].map(h => (
                        <th key={h} className="px-3 py-2 text-left uppercase tracking-wide font-medium" style={{ color: theme.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${theme.border}` }}>
                        <td className="px-3 py-2" style={{ color: theme.text }}>
                          {l.mode === 'catalog'
                            ? items.find(it => it.id === l.itemId)?.name ?? '—'
                            : l.freeText}
                          {l.mode === 'free_text' && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-900/40 text-yellow-400">Free text</span>
                          )}
                        </td>
                        <td className="px-3 py-2" style={{ color: theme.text }}>{l.qty}</td>
                        <td className="px-3 py-2 capitalize" style={{ color: theme.muted }}>{l.condition}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(localError || error) && <p className="text-red-400">{localError || error}</p>}
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-between px-5 py-3 flex-shrink-0"
          style={{ borderTop: `1px solid ${theme.border}` }}
        >
          <div>
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(s => s - 1)}
                className="text-sm px-3 py-1.5 rounded border"
                style={{ borderColor: theme.border, color: theme.muted }}
              >
                ← Back
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={close} className="text-xs px-3 py-1.5 rounded border" style={{ borderColor: theme.border, color: theme.muted }}>
              Cancel
            </button>
            {step < 2 ? (
              <button
                type="button"
                disabled={step === 0 ? !step1Valid : !step2Valid}
                onClick={() => setStep(s => s + 1)}
                className="text-xs px-4 py-1.5 rounded bg-blue-600 text-white font-medium disabled:opacity-40"
              >
                Next →
              </button>
            ) : (
              <button
                type="button"
                disabled={saving}
                onClick={handleSubmit}
                className="text-xs px-4 py-1.5 rounded bg-blue-600 text-white font-medium disabled:opacity-50"
              >
                {saving ? 'Submitting…' : 'Submit Request'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NewRequestWizard;
